import { Pressable } from 'react-native';

import { Stack } from '@/components/layout';
import { Avatar, Text } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';
import type { GuardianEventRowProps } from './types';

type DayPanelEventRowProps = Pick<
  GuardianEventRowProps,
  | 'accessibilityLabel'
  | 'accentColor'
  | 'bookingLabel'
  | 'isBookedByCurrentGuardian'
  | 'onPress'
  | 'playerAvatar'
  | 'playerName'
  | 'timeLabel'
  | 'title'
>;

export default function DayPanelEventRow({
  accessibilityLabel,
  accentColor,
  bookingLabel,
  isBookedByCurrentGuardian = false,
  onPress,
  playerAvatar,
  playerName,
  timeLabel,
  title,
}: DayPanelEventRowProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={
        accessibilityLabel ?? `${timeLabel ?? ''} · ${title} · ${bookingLabel ?? playerName}`
      }
      onPress={onPress}
      style={({ pressed }) => [
        styles.root,
        isBookedByCurrentGuardian ? styles.currentGuardianBooking : styles.otherGuardianBooking,
        pressed ? styles.pressed : null,
      ]}>
      <Stack direction="row" align="center" gap={scale(6)}>
        <Stack
          width={scale(2)}
          height={scale(18)}
          borderRadius={scale(999)}
          backgroundColor={accentColor}
        />
        <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.time}>
          {timeLabel}
        </Text>
        <Stack flex={1} />
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
          style={[
            styles.booking,
            isBookedByCurrentGuardian ? styles.currentGuardianBookingText : null,
          ]}>
          {bookingLabel}
        </Text>
      </Stack>

      <Stack direction="row" align="center" gap={scale(8)}>
        <Avatar size={scale(20)} borderWidth={0} activeOpacity={1}>
          <Avatar.Image source={playerAvatar} backgroundColor={guardianColors['2']} />
        </Avatar>
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
          numberOfLines={1}
          style={styles.title}>
          {title}
        </Text>
      </Stack>
    </Pressable>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    gap: scale(4),
    borderRadius: scale(12),
    paddingHorizontal: scale(12),
    paddingVertical: scale(8),
  },
  currentGuardianBooking: {
    backgroundColor: '#DDF7F1',
  },
  otherGuardianBooking: {
    backgroundColor: '#E4EBFC',
  },
  pressed: {
    opacity: 0.76,
  },
  time: {
    color: guardianColors['6'],
  },
  booking: {
    color: guardianColors['6'],
  },
  currentGuardianBookingText: {
    color: '#16A34A',
  },
  title: {
    flexShrink: 1,
    color: guardianColors['10'],
  },
}));
